import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Trash2, Printer, Copy, Save, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { qd, money, apiError, ENTITY_BY_COUNTRY, STATUS_COLORS, EntityLogo } from '../api';

const DEFAULT_CONDITIONS = 'Quote valid 30 days. Prices excl. VAT, ex-warehouse. Discount subject to firm order.';
const CATS = ['All', 'Gateway', 'Sensor', 'Accessory', 'Services', 'Software'];

export default function QdQuoteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [quote, setQuote] = useState(null);
  const [clientId, setClientId] = useState('');
  const [entityId, setEntityId] = useState('');
  const [items, setItems] = useState([]);
  const [discountPct, setDiscountPct] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  const [vatRate, setVatRate] = useState(21);
  const [conditions, setConditions] = useState(DEFAULT_CONDITIONS);
  const [language, setLanguage] = useState('en');
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('All');
  const [free, setFree] = useState({ ref: '', name: '', price: '' });
  const [msg, setMsg] = useState('');
  const [catOpen, setCatOpen] = useState(true);

  useEffect(() => {
    Promise.all([qd.get('/entities'), qd.get('/clients'), qd.get('/products')]).then(([e, c, p]) => {
      setEntities(e.data); setClients(c.data); setProducts(p.data.filter((x) => x.active));
    });
    if (id) {
      qd.get(`/quotes/${id}`).then((r) => {
        const q = r.data;
        setQuote(q); setClientId(q.client_id); setEntityId(q.entity_id); setItems(q.items);
        setDiscountPct(q.discount_pct); setDiscountReason(q.discount_reason);
        setVatRate(q.vat_rate); setConditions(q.conditions); setLanguage(q.language);
      });
    }
  }, [id]);

  const client = clients.find((c) => c.id === clientId);
  const entity = entities.find((e) => e.id === entityId);

  const onClientChange = (cid) => {
    setClientId(cid);
    const c = clients.find((x) => x.id === cid);
    if (!c) return;
    const eid = ENTITY_BY_COUNTRY[c.country];
    if (eid) setEntityId(eid);
    const ent = entities.find((x) => x.id === (eid || entityId));
    // Reverse charge if client has VAT number in a different country than the selling entity
    if (c.vat_number && ent && !c.vat_number.toUpperCase().startsWith(ent.country)) setVatRate(0);
    else setVatRate(21);
  };

  const addProduct = (p) => {
    setItems((prev) => {
      const i = prev.findIndex((x) => x.ref === p.ref);
      if (i >= 0) { const n = [...prev]; n[i] = { ...n[i], qty: n[i].qty + 1 }; return n; }
      return [...prev, { ref: p.ref, name: p.name, qty: 1, unit_price: p.price }];
    });
  };

  const updateItem = (i, field, value) => {
    setItems((prev) => { const n = [...prev]; n[i] = { ...n[i], [field]: value }; return n; });
  };

  const addFreeLine = () => {
    if (!free.ref || !free.name) return;
    setItems((prev) => [...prev, { ref: free.ref, name: free.name, qty: 1, unit_price: Number(free.price) || 0 }]);
    setFree({ ref: '', name: '', price: '' });
  };

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.qty * i.unit_price, 0);
    const discount = subtotal * (Number(discountPct) || 0) / 100;
    const net = subtotal - discount;
    const vat = net * (Number(vatRate) || 0) / 100;
    return { subtotal, discount, net, vat, total: net + vat };
  }, [items, discountPct, vatRate]);

  const payload = {
    client_id: clientId, entity_id: entityId, language,
    items: items.map((i) => ({ ...i, qty: Number(i.qty) || 1, unit_price: Number(i.unit_price) || 0 })),
    discount_pct: Number(discountPct) || 0, discount_reason: discountReason,
    vat_rate: Number(vatRate) || 0, conditions,
  };

  const save = async () => {
    setMsg('');
    try {
      if (quote) {
        const { data } = await qd.patch(`/quotes/${quote.id}`, payload);
        setQuote(data); setMsg('Saved.');
      } else {
        if (!clientId || !entityId) { setMsg('Select a client first.'); return; }
        const { data } = await qd.post('/quotes', payload);
        navigate(`/quote/devis/${data.id}`, { replace: true });
        setQuote(data); setMsg(`Created ${data.number}`);
      }
    } catch (e) { setMsg(apiError(e)); }
  };

  const setStatus = async (status) => {
    if (!quote) return;
    const { data } = await qd.patch(`/quotes/${quote.id}`, { status });
    setQuote(data); setMsg(`Status: ${status}`);
  };

  const duplicate = async () => {
    if (!quote) return;
    const { data } = await qd.post(`/quotes/${quote.id}/duplicate`);
    navigate(`/quote/devis/${data.id}`);
    setQuote(data); setItems(data.items); setMsg(`Duplicated as ${data.number}`);
  };

  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendCc, setSendCc] = useState('');
  const [sendMode, setSendMode] = useState('standard');
  const [sendText, setSendText] = useState('');
  const [sending, setSending] = useState(false);

  const standardPreview = quote
    ? `Dear customer,\n\nPlease find attached our quotation ${quote.number} (total ${Number(quote.totals?.net || 0).toFixed(2)} EUR excl. VAT, valid ${quote.validity_days || 30} days).\n\nBest regards,\n${entity?.name || ''}`
    : '';

  const sendQuote = () => {
    if (!quote) { setMsg('Save the quote first.'); return; }
    setSendTo(client?.contact_email || '');
    setSendMode('standard');
    setSendText('');
    setSendOpen(true);
  };

  const doSend = async () => {
    if (!sendTo || sending) return;
    setSending(true);
    setMsg('Sending…');
    try {
      const { data } = await qd.post(`/quotes/${quote.id}/send`, {
        to_email: sendTo,
        message: sendMode === 'custom' ? sendText : '',
      });
      setQuote({ ...quote, status: 'sent' });
      setMsg(`Sent to ${data.sent_to} (CC: ${data.cc.join(', ')})`);
      setSendOpen(false);
    } catch (e) { setMsg(apiError(e)); }
    finally { setSending(false); }
  };

  const filtered = products.filter((p) =>
    (cat === 'All' || p.category === cat) &&
    (p.ref.toLowerCase().includes(search.toLowerCase()) || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Left pane: catalog */}
      {catOpen ? (
      <div className="w-80 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col no-print">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Catalog</p>
            <button onClick={() => setCatOpen(false)} title="Collapse catalog" className="text-slate-400 hover:text-slate-700" data-testid="qd-catalog-collapse">
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
          <div className="relative mb-2">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-2.5" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ref or name…"
              className="w-full border border-slate-300 rounded pl-8 pr-2 py-2 text-sm" data-testid="qd-catalog-search" />
          </div>
          <div className="flex gap-1 flex-wrap">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)}
                className={`px-2 py-1 text-[11px] rounded border ${cat === c ? 'bg-[#14233F] text-white border-[#14233F]' : 'border-slate-200 text-slate-500'}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.map((p) => (
            <button key={p.id} onClick={() => addProduct(p)}
              className="w-full text-left px-4 py-2.5 border-b border-slate-50 hover:bg-teal-50 group"
              data-testid={`qd-product-${p.ref}`}>
              <div className="flex justify-between items-baseline">
                <span className="mono text-[11px] text-slate-400">{p.ref}</span>
                <span className="mono text-xs font-semibold text-[#14233F]">{money(p.price)}</span>
              </div>
              <p className="text-sm text-slate-700 group-hover:text-[#0F7D70]">{p.name}</p>
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-slate-200 bg-slate-50">
          <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1.5">Free line (off-catalog)</p>
          <div className="flex gap-1.5 mb-1.5">
            <input value={free.ref} onChange={(e) => setFree({ ...free, ref: e.target.value })} placeholder="Ref"
              className="w-20 border border-slate-300 rounded px-2 py-1.5 text-xs mono" data-testid="qd-free-ref" />
            <input value={free.name} onChange={(e) => setFree({ ...free, name: e.target.value })} placeholder="Description"
              className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-xs" data-testid="qd-free-name" />
          </div>
          <div className="flex gap-1.5">
            <input value={free.price} onChange={(e) => setFree({ ...free, price: e.target.value })} placeholder="Price €" type="number"
              className="w-24 border border-slate-300 rounded px-2 py-1.5 text-xs mono" data-testid="qd-free-price" />
            <button onClick={addFreeLine} className="flex-1 bg-[#14233F] text-white text-xs rounded py-1.5" data-testid="qd-free-add">Add line</button>
          </div>
        </div>
      </div>
      ) : (
      <button onClick={() => setCatOpen(true)} title="Open catalog"
        className="w-10 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col items-center pt-4 gap-2 text-slate-400 hover:text-[#0F7D70] no-print"
        data-testid="qd-catalog-expand">
        <PanelLeftOpen className="h-5 w-5" />
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ writingMode: 'vertical-rl' }}>Catalog</span>
      </button>
      )}

      {/* Right pane: quote */}
      <div className="flex-1 overflow-y-auto">
        {/* Toolbar */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-2 flex-wrap no-print">
          <select value={clientId} onChange={(e) => onClientChange(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1.5 text-sm max-w-[200px]" data-testid="qd-client-select">
            <option value="">— Select client —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.country})</option>)}
          </select>
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="qd-entity-select">
            <option value="">— Entity —</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <select value={language} onChange={(e) => setLanguage(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1.5 text-sm" data-testid="qd-language-select">
            <option value="en">EN</option><option value="lt">LT</option><option value="lv">LV</option><option value="et">ET</option>
          </select>
          <div className="flex-1" />
          {quote && (
            <>
              <span className={`px-2 py-1 rounded text-[11px] font-semibold ${STATUS_COLORS[quote.status]}`} data-testid="qd-quote-status">{quote.status}</span>
              <select value={quote.status} onChange={(e) => setStatus(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1.5 text-xs" data-testid="qd-status-select">
                {['draft', 'sent', 'accepted', 'expired'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={duplicate} className="p-2 border border-slate-300 rounded hover:bg-slate-50" title="Duplicate / revise" data-testid="qd-duplicate-btn">
                <Copy className="h-4 w-4 text-slate-600" />
              </button>
            </>
          )}
          <button onClick={() => window.print()} className="p-2 border border-slate-300 rounded hover:bg-slate-50" title="Print / PDF" data-testid="qd-print-btn">
            <Printer className="h-4 w-4 text-slate-600" />
          </button>
          <button onClick={sendQuote} className="flex items-center gap-1.5 bg-[#14233F] text-white px-4 py-2 rounded text-sm font-semibold" data-testid="qd-send-btn">
            Send
          </button>
          <button onClick={save} className="flex items-center gap-1.5 bg-[#0F7D70] text-white px-4 py-2 rounded text-sm font-semibold" data-testid="qd-save-btn">
            <Save className="h-4 w-4" /> {quote ? 'Save' : 'Create'}
          </button>
        </div>
        {msg && <p className="px-6 pt-2 text-xs text-[#0F7D70] no-print" data-testid="qd-editor-msg">{msg}</p>}

        {/* Send modal */}
        {sendOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 no-print" data-testid="qd-send-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <h2 className="text-lg font-bold text-[#14233F] mb-4">Send quotation {quote?.number}</h2>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Recipient</label>
              <input value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="client@company.com" type="email"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm mb-4" data-testid="qd-send-to" />
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Cc <span className="normal-case font-normal text-slate-400">(optional, separate with commas)</span></label>
              <input value={sendCc} onChange={(e) => setSendCc(e.target.value)} placeholder="colleague@company.com, manager@company.com"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm mb-4" data-testid="qd-send-cc" />
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email message</label>
              <label className="flex items-start gap-2.5 border rounded p-3 mb-2 cursor-pointer text-sm border-slate-200 has-[:checked]:border-[#0F7D70] has-[:checked]:bg-teal-50/50">
                <input type="radio" name="sendMode" checked={sendMode === 'standard'} onChange={() => setSendMode('standard')} className="mt-0.5" data-testid="qd-send-mode-standard" />
                <span>
                  <span className="font-semibold text-slate-800 block">Standard message (default)</span>
                  <span className="text-xs text-slate-500 whitespace-pre-line block mt-1">{standardPreview}</span>
                </span>
              </label>
              <label className="flex items-start gap-2.5 border rounded p-3 mb-2 cursor-pointer text-sm border-slate-200 has-[:checked]:border-[#0F7D70] has-[:checked]:bg-teal-50/50">
                <input type="radio" name="sendMode" checked={sendMode === 'custom'} onChange={() => { setSendMode('custom'); if (!sendText) setSendText(standardPreview); }} className="mt-0.5" data-testid="qd-send-mode-custom" />
                <span className="font-semibold text-slate-800">Custom accompanying message</span>
              </label>
              {sendMode === 'custom' && (
                <textarea value={sendText} onChange={(e) => setSendText(e.target.value)} rows={7}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm mb-2 font-mono text-[13px]"
                  placeholder="Your message…" data-testid="qd-send-text" />
              )}
              <p className="text-[11px] text-slate-400 mb-4">The PDF quotation is always attached. Your email and eric@0gbaltics.com are automatically added in CC, plus any addresses above.</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setSendOpen(false)} className="border border-slate-300 px-4 py-2 rounded text-sm text-slate-600" data-testid="qd-send-cancel">Cancel</button>
                <button onClick={doSend} disabled={!sendTo || sending || (sendMode === 'custom' && !sendText.trim())}
                  className="bg-[#14233F] text-white px-5 py-2 rounded text-sm font-semibold disabled:opacity-40" data-testid="qd-send-confirm">
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quote document */}
        <div className="p-6 print-area">
          <div className="bg-white border border-slate-200 rounded max-w-3xl mx-auto p-8 print:border-0">
            <div className="flex justify-between items-start pb-6 border-b border-slate-200 mb-6">
              <div>
                <EntityLogo entity={entity} className="h-9 mb-3" />
                {entity && (
                  <div className="text-[11px] text-slate-500 leading-relaxed">
                    <p className="font-semibold text-slate-700">{entity.name}</p>
                    <p>{entity.address}</p>
                    <p>Reg. {entity.reg_code}{entity.vat_number ? ` · VAT ${entity.vat_number}` : ''}</p>
                    <p>{entity.bank} · IBAN {entity.iban} · BIC {entity.bic}</p>
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-widest text-slate-400">Quotation</p>
                <p className="mono text-lg font-bold text-[#14233F]" data-testid="qd-quote-number">{quote?.number || 'DRAFT'}</p>
                <p className="mono text-xs text-slate-500">Date: {quote?.created_at?.slice(0, 10) || today}</p>
                <p className="mono text-xs text-slate-500">Valid: 30 days</p>
              </div>
            </div>

            {client && (
              <div className="mb-6 text-sm">
                <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Customer</p>
                <p className="font-semibold text-slate-800">{client.name}</p>
                <p className="text-xs text-slate-500">{client.address} · {client.country}{client.vat_number ? ` · VAT ${client.vat_number}` : ''}</p>
              </div>
            )}

            <table className="w-full text-sm mb-4">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-300">
                  <th className="text-left py-2">Ref</th>
                  <th className="text-left py-2">Description</th>
                  <th className="text-right py-2 w-16">Qty</th>
                  <th className="text-right py-2 w-28">Unit €</th>
                  <th className="text-right py-2 w-28">Total €</th>
                  <th className="w-8 no-print" />
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`qd-line-${i}`}>
                    <td className="py-2 mono text-xs text-slate-500">{it.ref}</td>
                    <td className="py-2 text-slate-800">{it.name}</td>
                    <td className="py-2 text-right">
                      <input type="number" min="1" value={it.qty} onChange={(e) => updateItem(i, 'qty', Number(e.target.value))}
                        className="w-14 border border-slate-200 rounded px-1.5 py-1 text-right mono text-xs no-print" data-testid={`qd-qty-${i}`} />
                      <span className="hidden print:inline mono text-xs">{it.qty}</span>
                    </td>
                    <td className="py-2 text-right">
                      <input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, 'unit_price', Number(e.target.value))}
                        className="w-24 border border-slate-200 rounded px-1.5 py-1 text-right mono text-xs no-print" data-testid={`qd-price-${i}`} />
                      <span className="hidden print:inline mono text-xs">{money(it.unit_price)}</span>
                    </td>
                    <td className="py-2 text-right mono text-xs font-semibold">{money(it.qty * it.unit_price)}</td>
                    <td className="py-2 text-right no-print">
                      <button onClick={() => setItems(items.filter((_, x) => x !== i))} data-testid={`qd-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-red-500" />
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan="6" className="py-8 text-center text-slate-300 text-sm">Add products from the catalog on the left.</td></tr>
                )}
              </tbody>
            </table>

            <div className="flex justify-between gap-8">
              <div className="flex-1 no-print">
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-xs text-slate-500" htmlFor="qd-discount">Discount %</label>
                  <input id="qd-discount" type="number" min="0" max="100" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)}
                    className="w-16 border border-slate-200 rounded px-1.5 py-1 mono text-xs text-right" data-testid="qd-discount-input" />
                  <input value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} placeholder="Reason (e.g. order before 31.07)"
                    className="flex-1 border border-slate-200 rounded px-2 py-1 text-xs" data-testid="qd-discount-reason" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-500" htmlFor="qd-vat">VAT %</label>
                  <input id="qd-vat" type="number" min="0" max="30" value={vatRate} onChange={(e) => setVatRate(e.target.value)}
                    className="w-16 border border-slate-200 rounded px-1.5 py-1 mono text-xs text-right" data-testid="qd-vat-input" />
                  <span className="text-[11px] text-slate-400">{Number(vatRate) === 0 ? 'Intra-EU reverse charge (Art. 196 Dir. 2006/112/EC)' : 'Local VAT'}</span>
                </div>
              </div>
              <div className="w-64 text-sm">
                <div className="flex justify-between py-1 text-slate-500"><span>Subtotal</span><span className="mono">{money(totals.subtotal)}</span></div>
                {totals.discount > 0 && (
                  <div className="flex justify-between py-1 text-slate-500"><span>Discount {discountPct}%</span><span className="mono">−{money(totals.discount)}</span></div>
                )}
                <div className="flex justify-between py-1 font-semibold text-slate-800 border-t border-slate-200"><span>Net (excl. VAT)</span><span className="mono" data-testid="qd-total-net">{money(totals.net)}</span></div>
                <div className="flex justify-between py-1 text-slate-500"><span>VAT {vatRate}%</span><span className="mono">{money(totals.vat)}</span></div>
                <div className="flex justify-between py-2 font-bold text-[#14233F] border-t-2 border-[#14233F] text-base"><span>Total</span><span className="mono" data-testid="qd-total">{money(totals.total)}</span></div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100">
              <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Terms &amp; conditions</p>
              <textarea value={conditions} onChange={(e) => setConditions(e.target.value)} rows="2"
                className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs text-slate-600 no-print" data-testid="qd-conditions" />
              <p className="hidden print:block text-xs text-slate-600">{conditions}</p>
              {discountReason && <p className="text-[11px] text-slate-400 mt-1">Discount condition: {discountReason}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
